import {
  AdditiveBlending,
  Group,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
  SphereGeometry,
  type BufferGeometry,
} from 'three'
import type { CharacterVisualState } from './SceneBridge'

/**
 * The state a fighter wears: the stance dome and the pickup bubble.
 *
 * These used to live inside the procedural rig, which meant that the moment
 * every fighter became an imported model they silently stopped being drawn —
 * the stance is one of the five buttons and it had no visual at all. Keeping
 * them here, on the holder rather than on the body, means they survive any
 * future swap of what a fighter is made of.
 *
 * They are also deliberately *louder* than the rig's versions were. Those ran at
 * 0.05–0.15 opacity: technically present, invisible in play. A defensive state
 * that cannot be seen from across the arena is a state the other player cannot
 * respect, and then the counter-play everyone designed around it never happens.
 */
export class FighterAura {
  readonly group = new Group()

  private readonly braceDome: Mesh
  private readonly braceRing: Mesh
  private readonly bubble: Mesh
  private clock = 0

  /** Owns its geometry: six fighters × two shells is not worth sharing. */
  private readonly owned: BufferGeometry[] = []

  constructor() {
    // Sized to the 2.6 m the renderer stands a fighter at, not to the 1.2 m rig.
    // A sparse cage, not a shell: at 20 × 10 segments the wireframe is dense
    // enough that additive blending plus bloom fills it in solid, and the frame
    // showed a glowing white egg with the fighter nowhere to be seen. The dome
    // has to say "protected", not replace the thing being protected.
    this.braceDome = this.shell(
      new SphereGeometry(1.5, 12, 5, 0, Math.PI * 2, 0, 1.35),
      0xdcd6c4,
      true,
    )
    this.braceDome.position.y = 0.35

    // The dome alone is hard to read against a busy floor from directly above,
    // which is exactly the angle this camera uses. The ring on the ground is the
    // part that survives the top-down view.
    this.braceRing = this.shell(new RingGeometry(1.35, 1.62, 40), 0xdcd6c4, false)
    this.braceRing.rotation.x = -Math.PI / 2
    this.braceRing.position.y = 0.06

    this.bubble = this.shell(new SphereGeometry(1.7, 14, 8), 0x7fe6ff, true)
    this.bubble.position.y = 1.25

    this.group.add(this.braceDome, this.braceRing, this.bubble)
  }

  update(state: CharacterVisualState, dt: number): void {
    this.clock += dt

    // Bright and wide at full charge, tight and flickering as it runs out, so
    // "how long have I got" never needs a glance at the HUD.
    const bracing = state.bracing && !state.falling
    this.braceDome.visible = bracing
    this.braceRing.visible = bracing
    if (bracing) {
      const left = clamp01(state.braceFraction)
      const dying = left < 0.25 ? 0.5 + 0.5 * Math.sin(this.clock * 35) : 1
      mat(this.braceDome).opacity = (0.05 + left * 0.1) * dying
      mat(this.braceRing).opacity = (0.28 + left * 0.34) * dying
      this.braceDome.scale.setScalar(0.86 + left * 0.24)
      this.braceDome.rotation.y += dt * 1.6
      this.braceRing.scale.setScalar(0.9 + left * 0.18)
    }

    this.bubble.visible = state.shield > 0 && !state.falling
    if (this.bubble.visible) {
      const dying = state.shield < 1.5 ? 0.35 + 0.65 * Math.abs(Math.sin(this.clock * 20)) : 1
      mat(this.bubble).opacity = 0.16 * dying
      this.bubble.rotation.y -= dt * 0.9
    }
  }

  dispose(): void {
    for (const geometry of this.owned) geometry.dispose()
    for (const mesh of [this.braceDome, this.braceRing, this.bubble]) {
      ;(mesh.material as MeshBasicMaterial).dispose()
    }
  }

  private shell(geometry: BufferGeometry, color: number, wireframe: boolean): Mesh {
    this.owned.push(geometry)
    const mesh = new Mesh(
      geometry,
      new MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0,
        blending: AdditiveBlending,
        depthWrite: false,
        side: 2,
        wireframe,
        toneMapped: false,
      }),
    )
    mesh.visible = false
    return mesh
  }
}

function mat(mesh: Mesh): MeshBasicMaterial {
  return mesh.material as MeshBasicMaterial
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}
